import React from 'react';
import { useTranslation } from '../../i18n';
import { AppIcon } from './icons';

type PlayerRow = { id: string; name: string; score: number };

export interface HostPlayingScoreStripProps {
  hostIsPlaying: boolean;
  players: PlayerRow[];
  myPlayerId: string;
  pointsThisQuestion?: number | null;
  showQuestionPointsLine?: boolean;
  /** When set, strip is a toggle for the full standings list. */
  expanded?: boolean;
  onToggleExpand?: () => void;
}

export const HostPlayingScoreStrip: React.FC<HostPlayingScoreStripProps> = ({
  hostIsPlaying,
  players,
  myPlayerId,
  pointsThisQuestion = null,
  showQuestionPointsLine = false,
  expanded,
  onToggleExpand,
}) => {
  const { t } = useTranslation();

  if (!hostIsPlaying || !players?.length || !myPlayerId) {
    return null;
  }

  const sorted = [...players].sort((a, b) => b.score - a.score);
  const rank = sorted.findIndex((p) => p.id === myPlayerId) + 1;
  const me = sorted.find((p) => p.id === myPlayerId);
  if (!me || rank < 1) {
    return null;
  }

  const showPtsLine = showQuestionPointsLine && pointsThisQuestion !== null && pointsThisQuestion !== undefined;
  const isToggle = typeof onToggleExpand === 'function';
  const isExpanded = !!expanded;

  const content = (
    <>
      <span
        style={{
          display: 'flex',
          alignItems: 'center',
          gap: '0.5rem 0.75rem',
          minWidth: 0,
          flex: 1,
          flexWrap: 'wrap',
        }}
      >
        <span style={{ fontWeight: 700, color: 'var(--color-primary)', flexShrink: 0 }}>
          {t('gameplay.hostScoreStrip.rankOf', { rank, total: players.length })}
        </span>
        <span
          style={{
            fontWeight: 600,
            color: 'var(--text-primary)',
            overflow: 'hidden',
            textOverflow: 'ellipsis',
            whiteSpace: 'nowrap',
            minWidth: 0,
          }}
          title={me.name}
        >
          {me.name}
        </span>
        <span style={{ opacity: 0.85, flexShrink: 0 }}>
          {t('gameplay.hostScoreStrip.ptsTotal', { score: me.score })}
        </span>
        {showPtsLine && (
          <span style={{ fontWeight: 600, color: '#34D399', flexShrink: 0 }}>
            {t('gameplay.hostScoreStrip.thisQuestion', { points: pointsThisQuestion })}
          </span>
        )}
      </span>
      {isToggle && (
        <AppIcon
          name="expandMore"
          fontSize="small"
          aria-hidden
          style={{
            flexShrink: 0,
            color: 'var(--color-primary)',
            transform: isExpanded ? 'rotate(180deg)' : 'rotate(0deg)',
            transition: 'transform 0.2s ease',
          }}
        />
      )}
    </>
  );

  const sharedStyle: React.CSSProperties = {
    width: '100%',
    marginTop: '0.35rem',
    marginBottom: isExpanded ? '0.35rem' : '0.75rem',
    padding: '0.65rem 0.85rem',
    borderRadius: '10px',
    backgroundColor: 'rgba(255, 255, 255, 0.88)',
    border: '1.5px solid rgba(var(--color-primary-rgb), 0.25)',
    display: 'flex',
    flexWrap: 'nowrap',
    alignItems: 'center',
    justifyContent: isToggle ? 'flex-start' : 'center',
    gap: '0.5rem 0.75rem',
    fontSize: '0.85rem',
    color: 'var(--text-primary)',
    boxSizing: 'border-box',
    boxShadow: 'var(--shadow-sm)',
  };

  if (isToggle) {
    return (
      <button
        type="button"
        className="host-playing-score-strip host-playing-score-strip--toggle"
        onClick={onToggleExpand}
        aria-expanded={isExpanded}
        aria-label={
          isExpanded
            ? t('gameplay.hostScoreStrip.collapseStandings')
            : t('gameplay.hostScoreStrip.expandStandings')
        }
        style={{
          ...sharedStyle,
          cursor: 'pointer',
          font: 'inherit',
          textAlign: 'left',
          appearance: 'none',
          WebkitAppearance: 'none',
        }}
      >
        {content}
      </button>
    );
  }

  return (
    <div className="host-playing-score-strip" style={sharedStyle}>
      {content}
    </div>
  );
};
